import React, { useEffect, useRef, useState } from 'react';
import api from 'src/services/BaseService';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  ListSubheader,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddEditPorterModal from 'src/components/modals/AddEditPorterModal';
import { toast } from 'react-toastify';
import { tokenService } from 'src/services';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTheme } from '@mui/material/styles';

// shared
import ClearButton from 'src/components/shared/ClearButton';
import FilterButton from 'src/components/shared/FilterButton';
import AddEntityButton from 'src/components/shared/AddEntityButton';
import EditButton from 'src/components/shared/EditButton';
import HorizontalDivider from 'src/components/shared/HorizontalDivider';
import UserStatusFilters from 'src/components/shared/UserStatusFilters';
import ExportToExcelButton from 'src/components/shared/ExportToExcelButton';

import DynamicTable from 'src/components/shared/DynamicTable';
import CustomFilter from 'src/components/filter/CustomFilter';
import FilterViewButton from 'src/components/shared/FilterViewButton';
import { useLocalization } from 'src/context/LocalizationContext';

import { ReactComponent as GetirLogo } from 'src/assets/images/Getir_id6ORl3E2g_0.svg';

const MallFilter = React.memo(
  ({
    userMalls,
    selectedMalls,
    onChangeMall,
    onSelectAll,
    onClearAll,
    menuProps,
  }) => {
    const [mallSearch, setMallSearch] = useState('');
    const searchRef = useRef(null);

    // MUI steals focus to the first MenuItem when the list re-renders.
    // After every mallSearch change, give focus back to the input.
    useEffect(() => {
      if (searchRef.current) {
        searchRef.current.focus();
      }
    }, [mallSearch]);

    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ flexWrap: 'nowrap' }}
      >
        <FormControl size="small" sx={{ minWidth: 160, maxWidth: 280 }}>
          <InputLabel>AVM</InputLabel>
          <Select
            multiple
            value={selectedMalls}
            onChange={onChangeMall}
            input={<OutlinedInput label="AVM" />}
            MenuProps={{
              ...menuProps,
              disableAutoFocusItem: true,
              PaperProps: {
                ...menuProps.PaperProps,
                style: { ...menuProps.PaperProps.style, maxHeight: 300 },
              },
            }}
            onClose={() => setMallSearch('')}
            renderValue={(selected) => {
              if (selected.length === userMalls.length) {
                return (
                  <Chip
                    label="Tümü"
                    size="small"
                    sx={{ fontSize: 11, height: 20 }}
                  />
                );
              }
              return (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {selected.slice(0, 2).map((id) => {
                    const mall = userMalls.find((m) => m.id === id);
                    return (
                      <Chip
                        key={id}
                        label={mall?.title || id}
                        size="small"
                        sx={{ fontSize: 11, height: 20 }}
                      />
                    );
                  })}
                  {selected.length > 2 && (
                    <Chip
                      label={`+${selected.length - 2}`}
                      size="small"
                      sx={{ fontSize: 11, height: 20 }}
                    />
                  )}
                </Stack>
              );
            }}
          >
            <ListSubheader
              sx={{ p: 1, lineHeight: 'unset' }}
              onClickCapture={(e) => e.stopPropagation()}
            >
              <TextField
                inputRef={searchRef}
                size="small"
                placeholder="AVM ara..."
                fullWidth
                value={mallSearch}
                onChange={(e) => setMallSearch(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </ListSubheader>
            {userMalls
              .filter((mall) =>
                mall.title.toLowerCase().includes(mallSearch.toLowerCase())
              )
              .map((mall) => {
                const isSelected = selectedMalls.includes(mall.id);
                return (
                  <MenuItem key={mall.id} value={mall.id}>
                    <CheckIcon
                      sx={{
                        fontSize: 16,
                        mr: 1,
                        color: isSelected ? '#22c55e' : 'transparent',
                      }}
                    />
                    {mall.title}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
        <Button size="small" variant="outlined" onClick={onSelectAll}>
          Tümünü Seç
        </Button>
        <Button size="small" variant="outlined" onClick={onClearAll}>
          Tümünü Kaldır
        </Button>
      </Stack>
    );
  }
);

const GetirIcon = ({ active, onClick }) => {
  return (
    <div
      onClick={active ? undefined : onClick}
      style={{
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: active ? 'default' : 'pointer',
        filter: active ? null : 'grayscale(1)',
        opacity: active ? 1 : 0.5,
      }}
    >
      <GetirLogo style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default function Porters() {
  const { language } = useLocalization();

  // authLocations'dan gelen AVM listesi (id + title içeriyor), alfabetik sıralı
  const userMalls = [
    ...(tokenService.getUser()?.authLocations?.mall || []),
  ].sort((a, b) => (a.title || '').localeCompare(b.title || '', 'tr'));
  const allMallIDs = userMalls.map((m) => m.id);

  const [willShowStatus, setWillShowStatus] = useState('1');
  const [porterList, setPorterList] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [selectedMalls, setSelectedMalls] = useState(allMallIDs);
  const [isModalEdit, setIsModalEdit] = useState(false);
  const [editData, setEditData] = useState({});
  const [isAuth, setIsAuth] = useState(false);
  const [mallList, setMallList] = useState([]);
  const [porterUsername, setPorterUsername] = useState('');
  const [porterName, setPorterName] = useState('');
  const [porterLastname, setPorterLastname] = useState('');
  const [porterAuths, setPorterAuths] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [porterBranchList, setPorterBranchList] = useState([]);
  const [createdStartDate, setCreatedStartDate] = useState();
  const [createdEndDate, setCreatedEndDate] = useState();
  const [statusChangeStartDate, setStatusChangeStartDate] = useState();
  const [statusChangeEndDate, setStatusChangeEndDate] = useState();
  const [tableHead, setTableHead] = useState([]);
  const isMobile = useMediaQuery('(max-width:768px)');
  const [filterModal, setFilterModal] = useState(false);
  const [showPopUpError, setShowPopUpError] = useState(false);

  // Getir dialog state
  const [getirDialogOpen, setGetirDialogOpen] = useState(false);
  const [selectedPorter, setSelectedPorter] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const [pointsModalOpen, setPointsModalOpen] = useState(false);
  const [selectedPoints, setSelectedPoints] = useState([]);

  const theme = useTheme();

  const authList = [
    { label: 'Yönetici', id: 'manager' },
    { label: 'Operasyon', id: 'operations' },
    { label: 'İptal Yetkisi', id: 'can-cancel' },
  ];
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 0;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  function getStyles(name, personName, theme) {
    return {
      fontWeight:
        personName.indexOf(name) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium,
    };
  }

  function arrayContainsAllElements(mainArray, subArray) {
    return subArray.every((element) => mainArray.includes(element));
  }

  const openPointsModal = (points) => {
    setSelectedPoints(points || []);
    setPointsModalOpen(true);
  };

  const getFilteredPorterList = () => {
    const filteredPorterList = originalData.filter((item) => {
      // Tüm AVM'ler seçiliyse AVM filtresini atla.
      // Subset seçiliyse hem raw-ID hem obje formatını tolere et.
      if (selectedMalls.length === 0) return false;
      if (selectedMalls.length < allMallIDs.length) {
        const malls = item.authLocations?.mall || [];
        const mallMatch = malls.some((m) => {
          const id = m && typeof m === 'object' ? m.id : m;
          return selectedMalls.includes(id);
        });
        if (!mallMatch) return false;
      }

      const usernameMatch = porterUsername
        ? item.username.toLowerCase().includes(porterUsername.toLowerCase())
        : true;
      const nameMatch = porterName
        ? item.name.toLowerCase().includes(porterName.toLowerCase())
        : true;
      const lastnameMatch = porterLastname
        ? item.lastname.toLowerCase().includes(porterLastname.toLowerCase())
        : true;

      const allowedPointsIDs = item.allowedPoints.map((point) => point.id);
      const hasMatchingPoints =
        porterBranchList.length > 0
          ? porterBranchList.some((id) => allowedPointsIDs.includes(id))
          : true;

      const hasMatchingAuths =
        porterAuths.length > 0
          ? arrayContainsAllElements(item.frontAuth, porterAuths)
          : true;

      const createdDateMatch =
        createdStartDate && createdEndDate
          ? moment.unix(item.createdDateTime).format('DD-MM-YYYY') >=
              moment(createdStartDate).format('DD-MM-YYYY') &&
            moment.unix(item.createdDateTime).format('DD-MM-YYYY') <=
              moment(createdEndDate).format('DD-MM-YYYY')
          : true;
      const statusChangedDateMatch =
        statusChangeStartDate && statusChangeEndDate
          ? moment.unix(item.lastStatusUpdateDateTime).format('DD-MM-YYYY') >=
              moment(statusChangeStartDate).format('DD-MM-YYYY') &&
            moment.unix(item.lastStatusUpdateDateTime).format('DD-MM-YYYY') <=
              moment(statusChangeEndDate).format('DD-MM-YYYY')
          : true;

      return (
        usernameMatch &&
        nameMatch &&
        lastnameMatch &&
        hasMatchingPoints &&
        hasMatchingAuths &&
        createdDateMatch &&
        statusChangedDateMatch
      );
    });

    setPorterList(filteredPorterList);
  };

  const clearFilters = () => {
    setPorterUsername('');
    setPorterName('');
    setPorterLastname('');
    setPorterAuths([]);
    setPorterBranchList([]);
    setStatusChangeStartDate(null);
    setStatusChangeEndDate(null);
    setCreatedStartDate(null);
    setCreatedEndDate(null);
    setSelectedMalls(allMallIDs);
    setPorterList(originalData);
  };

  const handleChangePorterAuth = (event) => {
    const {
      target: { value },
    } = event;
    setPorterAuths(typeof value === 'string' ? value.split(',') : value);
  };

  const handleChangePorterBranch = (event) => {
    const {
      target: { value },
    } = event;
    setPorterBranchList(typeof value === 'string' ? value.split(',') : value);
  };

  const handleEdit = (rowData) => {
    setEditData(rowData);
    setOpenCreateModal(true);
    setIsModalEdit(true);
  };

  const handleOpenPorterAdd = () => {
    setOpenCreateModal(true);
    setIsModalEdit(false);
  };

  const handleOpenGetirDialog = (row) => {
    setSelectedPorter(row);
    setGetirDialogOpen(true);
  };

  const handleCloseGetirDialog = () => {
    if (confirmLoading) return;
    setGetirDialogOpen(false);
    setSelectedPorter(null);
  };

  const handleConfirmGetir = () => {
    if (!selectedPorter) return;
    setConfirmLoading(true);
    api
      .post('flow/set-porter-getir-id', { userID: selectedPorter.id })
      .then((res) => {
        if (res.data && res.data.status) {
          toast.success("Kullanıcı Getir'e başarıyla kaydedildi.");
          handleCloseGetirDialog();
          fetchData(willShowStatus);
        } else {
          toast.error(
            (res.data && res.data.message) || 'İşlem sırasında bir hata oluştu.'
          );
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setConfirmLoading(false);
      });
  };

  const handleChangeMall = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedMalls(typeof value === 'string' ? value.split(',') : value);
  };

  const clearMallFilter = () => {
    setSelectedMalls([]);
  };

  const selectAllMalls = () => {
    setSelectedMalls(allMallIDs);
  };

  const fetchData = (status) => {
    if (!allMallIDs || allMallIDs.length === 0) return;

    api
      .post('users/get-users', {
        type: 'porter',
        mallID: allMallIDs,
        status: status,
      })
      .then((response) => {
        console.log('response: ', response.data);
        if (response.data.status) {
          setPorterList(response.data.result);
          setOriginalData(response.data.result);
        } else {
          toast.error('Kullanıcı bulunamadı');
          setPorterList([]);
          setOriginalData([]);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  function exportToExcel() {
    const temp1 = porterList.map((x, index) => {
      const frontAuthReplacements = {
        operations: 'Operasyon',
        robot: 'Robot',
        manager: 'Yönetici',
        'can-cancel': 'İptal Yetkisi',
      };
      const frontAuthText = Array.isArray(x.frontAuth)
        ? x.frontAuth.map((val) => frontAuthReplacements[val] || val).join(', ')
        : '';
      const allowedPointsText = Array.isArray(x.allowedPoints)
        ? x.allowedPoints.map((point) => point.title).join(', ')
        : '';

      return {
        No: index + 1,
        'Kullanıcı Adı': x.username || '-',
        Ad: x.name || '-',
        Soyad: x.lastname || '-',
        Yetkiler: frontAuthText || '-',
        'İzin Verilen Noktalar': allowedPointsText || '-',
        'Oluşturulma Tarihi': x.createdDateTime
          ? moment.unix(x.createdDateTime).format('DD/MM/YYYY HH:mm')
          : '-',
        'Son Durum Güncelleme Tarihi': x.lastStatusUpdateDateTime
          ? moment.unix(x.lastStatusUpdateDateTime).format('DD/MM/YYYY HH:mm')
          : x.createdDateTime
          ? moment.unix(x.createdDateTime).format('DD/MM/YYYY HH:mm')
          : '-',
      };
    });

    api
      .post('excel/export', {
        type: 'excel',
        content: temp1,
        header: {
          No: 'number',
          'Kullanıcı Adı': 'string',
          Ad: 'string',
          Soyad: 'string',
          Yetkiler: 'string',
          'İzin Verilen Noktalar': 'string',
          'Oluşturulma Tarihi': 'string',
          'Son Durum Güncelleme Tarihi': 'string',
        },
        sheetTitle: 'Porter Listesi',
      })
      .then((response) => {
        if (response.data.status) {
          try {
            const newWindow = window.open(response.data.result, '_blank');
            if (
              !newWindow ||
              newWindow.closed ||
              typeof newWindow.closed === 'undefined'
            ) {
              setShowPopUpError(true);
              throw new Error('Pop-up engellendi.');
            }
          } catch (e) {
            setShowPopUpError(true);
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  }

  // ── Effect 1: Sadece malls çek + initial porter fetch ──────────────
  useEffect(() => {
    api
      .post('malls/get-malls', { withOffices: true })
      .then((response) => {
        if (response.data.result) {
          setMallList(response.data.result);
          const currentMallBranches =
            response.data.result.filter(
              (item) => item.id === localStorage.getItem('mallID')
            )[0]?.offices || [];
          setBranchList(currentMallBranches);
          fetchData('1');
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 2: mallList dolunca tableHead'i oluştur ─────────────────
  // mallList bağımlılığı sayesinde AVM isimleri her zaman dolu gelir
  useEffect(() => {
    let showEdit = false;
    if (
      (tokenService.getUser()?.frontAuth.includes('manager') ||
        tokenService.getUser()?.frontAuth.includes('hr') ||
        tokenService.getUser()?.frontAuth.includes('regional-manager')) &&
      tokenService.getUser()?.accountCategory === 'mall-logistics'
    ) {
      setIsAuth(true);
      showEdit = true;
    }

    const frontAuthColumn = {
      id: 'frontAuth',
      label: language.commons.labels.frontAuth,
      alignRight: false,
      width: 10,
      format: (value) => {
        const frontAuthReplacements = {
          operations: 'Operasyon',
          robot: 'Robot',
          manager: 'Yönetici',
          'can-cancel': 'İptal Yetkisi',
        };
        return Array.isArray(value)
          ? value.map((val) => frontAuthReplacements[val] || val).join(', ')
          : '';
      },
    };

    const mallColumn = {
      id: 'authLocations',
      label: 'AVM',
      alignRight: false,
      format: (value) => {
        const mallIDs = value?.mall || [];
        const mallTitles = mallIDs
          .map((id) => {
            const mall = mallList?.find((m) => m.id === id);
            return mall ? mall.title : null;
          })
          .filter(Boolean);
        return mallTitles.join(', ');
      },
    };

    const allowedPointsColumn = {
      id: 'allowedPoints',
      label: language.commons.labels.allowedPoints,
      alignRight: false,
      format: (value) => {
        if (!value || value.length === 0) return '-';

        const preview = value.slice(0, 3);

        return (
          <div>
            {preview.map((point) => (
              <div key={point.title}>{point.title}</div>
            ))}

            {value?.length > 3 && (
              <Typography
                sx={{
                  color: '#1976d2',
                  cursor: 'pointer',
                  fontSize: 13,
                  mt: 0.5,
                }}
                onClick={() => openPointsModal(value)}
              >
                Daha fazla göster ({value.length})
              </Typography>
            )}
          </div>
        );
      },
    };

    const createdDateTimeColumn = {
      id: 'createdDateTime',
      label: language.commons.labels.createdDateTime,
      alignRight: false,
      format: (value) => moment.unix(value).format('DD/MM/YYYY HH:mm'),
    };

    const lastStatusUpdateColumn = {
      id: 'lastStatusUpdateDateTime',
      label: language.commons.labels.lastStatusUpdateDateTime,
      alignRight: false,
      format: (value, row) =>
        value
          ? moment.unix(value).format('DD/MM/YYYY HH:mm')
          : moment.unix(row.createdDateTime).format('DD/MM/YYYY HH:mm'),
    };

    const operationColumnWithEditAndGetir = {
      id: 'operation',
      label: '',
      alignRight: false,
      format: (value, row) => {
        const hasGetirId =
          row.getirID !== null &&
          row.getirID !== undefined &&
          String(row.getirID).trim() !== '';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <EditButton
              id={'porter_edit_icon'}
              onClick={() => handleEdit(row)}
            />
            <GetirIcon
              active={hasGetirId}
              onClick={() => {
                if (!hasGetirId) handleOpenGetirDialog(row);
              }}
            />
          </div>
        );
      },
      sx: { width: 140 },
    };

    const baseColumns = [
      {
        id: 'username',
        label: language.commons.labels.p_username,
        alignRight: false,
      },
      { id: 'name', label: language.commons.labels.name, alignRight: false },
      {
        id: 'lastname',
        label: language.commons.labels.lastname,
        alignRight: false,
      },
      frontAuthColumn,
      mallColumn,
      allowedPointsColumn,
      createdDateTimeColumn,
      lastStatusUpdateColumn,
    ];

    setTableHead(
      showEdit ? [...baseColumns, operationColumnWithEditAndGetir] : baseColumns
    );
  }, [mallList]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 3: AVM filtresi veya veri değişince anlık filtrele ─────────
  useEffect(() => {
    if (originalData.length === 0) return;
    getFilteredPorterList();
  }, [selectedMalls, originalData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShowPassive = () => {
    fetchData('0');
    setWillShowStatus('0');
  };

  const handleShowActive = () => {
    fetchData('1');
    setWillShowStatus('1');
  };

  return (
    <>
      <Helmet>
        <title> {language.porters.titles.site_title} </title>
      </Helmet>

      <Stack style={{ paddingInline: '25px' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            {language.porters.titles.porter_list}
          </Typography>
          <Stack direction="row" spacing={1}>
            {isAuth === true && (
              <AddEntityButton
                label={language.porters.titles.add_porter}
                onClick={() => handleOpenPorterAdd(true)}
              />
            )}
            <ExportToExcelButton
              setShowPopUpError={setShowPopUpError}
              showPopUpError={showPopUpError}
              onClick={() => exportToExcel()}
            />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          justifyContent={isMobile ? 'space-between' : 'end'}
          mb={5}
        >
          {isMobile && (
            <FilterViewButton
              variant="contained"
              onClick={() => setFilterModal(true)}
            />
          )}
        </Stack>

        <Card>
          <CustomFilter
            modalType={'drawer'}
            filterModal={filterModal}
            handleCloseModal={() => setFilterModal(false)}
          >
            <>
              <Stack
                direction="column"
                alignItems="center"
                justifyContent="space-between"
                paddingY={2}
                paddingX={1}
              >
                <Stack
                  width={'100%'}
                  direction="column"
                  alignItems="center"
                  justifyContent="space-between"
                  paddingY={2}
                  spacing={2}
                >
                  <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                    <TextField
                      label={language.commons.labels.p_username}
                      variant="outlined"
                      value={porterUsername}
                      onChange={(e) => setPorterUsername(e.target.value)}
                    />
                  </FormControl>
                  <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                    <TextField
                      label={language.commons.labels.name}
                      variant="outlined"
                      value={porterName}
                      onChange={(e) => setPorterName(e.target.value)}
                    />
                  </FormControl>
                  <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                    <TextField
                      label={language.commons.labels.lastname}
                      variant="outlined"
                      value={porterLastname}
                      onChange={(e) => setPorterLastname(e.target.value)}
                    />
                  </FormControl>

                  <FormControl sx={{ flex: 1, minWidth: '100%' }}>
                    <InputLabel>{language.commons.labels.frontAuth}</InputLabel>
                    <Select
                      multiple
                      value={porterAuths}
                      onChange={handleChangePorterAuth}
                      input={
                        <OutlinedInput
                          label={language.commons.labels.frontAuth}
                        />
                      }
                      MenuProps={MenuProps}
                    >
                      {authList.map((auth) => (
                        <MenuItem
                          key={auth.id}
                          value={auth.id}
                          style={getStyles(auth.label, porterAuths, theme)}
                        >
                          {auth.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ marginBottom: 2 }}
                >
                  <div style={{ marginTop: '10px' }}>
                    <Typography
                      style={{
                        fontFamily: 'Inter',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#6B7280',
                      }}
                    >
                      {language.commons.labels.createdDateTime}:{' '}
                    </Typography>
                    <div>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          sx={{ mt: 2, marginRight: '8px' }}
                          format="DD/MM/YYYY"
                          label={language.commons.labels.start_date}
                          value={createdStartDate}
                          onChange={(newValue) => setCreatedStartDate(newValue)}
                        />
                      </LocalizationProvider>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          sx={{ mt: 2, marginRight: '8px' }}
                          format="DD/MM/YYYY"
                          label={language.commons.labels.end_date}
                          value={createdEndDate}
                          onChange={(newValue) => setCreatedEndDate(newValue)}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div style={{ marginTop: '10px', marginLeft: '8px' }}>
                    <Typography
                      style={{
                        fontFamily: 'Inter',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#6B7280',
                      }}
                    >
                      {language.commons.labels.activation_deactivation}:{' '}
                    </Typography>
                    <div>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          sx={{ mt: 2, marginRight: '8px' }}
                          format="DD/MM/YYYY"
                          label={language.commons.labels.start_date}
                          value={statusChangeStartDate}
                          onChange={(newValue) =>
                            setStatusChangeStartDate(newValue)
                          }
                        />
                      </LocalizationProvider>
                      <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DatePicker
                          sx={{ mt: 2, marginRight: '8px' }}
                          format="DD/MM/YYYY"
                          label={language.commons.labels.end_date}
                          value={statusChangeEndDate}
                          onChange={(newValue) =>
                            setStatusChangeEndDate(newValue)
                          }
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                </Stack>
              </Stack>

              <HorizontalDivider />

              <Stack
                direction="column"
                alignItems="center"
                justifyContent="space-between"
                paddingY={2}
                paddingX={2}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  paddingY={2}
                  paddingX={2}
                >
                  <UserStatusFilters
                    activeOnClick={handleShowActive}
                    passiveOnClick={handleShowPassive}
                  />
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ width: '50%' }}
                >
                  <ClearButton
                    onClick={clearFilters}
                    sx={{ marginRight: '8px' }}
                  />
                  <FilterButton onClick={getFilteredPorterList} />
                </Stack>
              </Stack>
            </>
          </CustomFilter>

          {tableHead.length > 0 && (
            <DynamicTable
              data={porterList}
              headers={tableHead}
              initialOrder={'createdDateTime'}
              initalOrderDirection={'asc'}
              isOrdered={true}
              isMobile={isMobile}
              handleModal={() => setFilterModal(true)}
              handleCloseModal={() => setFilterModal(false)}
              rowOnClick={(row) => handleEdit(row)}
              leftHeaderSlot={
                userMalls.length > 1 ? (
                  <MallFilter
                    userMalls={userMalls}
                    selectedMalls={selectedMalls}
                    onChangeMall={handleChangeMall}
                    onSelectAll={selectAllMalls}
                    onClearAll={clearMallFilter}
                    menuProps={MenuProps}
                  />
                ) : null
              }
            />
          )}
        </Card>
      </Stack>

      {openCreateModal && (
        <AddEditPorterModal
          openCreateModal={openCreateModal}
          onCreateModal={() => setOpenCreateModal(false)}
          setIsModalEdit={() => setIsModalEdit()}
          isEdit={isModalEdit}
          editData={editData}
          pageRefresh={() => fetchData(willShowStatus)}
          malls={mallList}
        />
      )}

      {getirDialogOpen && selectedPorter && (
        <Dialog open={getirDialogOpen} onClose={handleCloseGetirDialog}>
          <DialogTitle>Getir Kaydı</DialogTitle>
          <DialogContent>
            <Typography>
              {selectedPorter.name} {selectedPorter.lastname} isimli kullanıcıyı
              Getir&apos;e kaydetmek istediğinize emin misiniz?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseGetirDialog} disabled={confirmLoading}>
              {language?.commons?.buttons?.no || 'Hayır'}
            </Button>
            <Button
              onClick={handleConfirmGetir}
              variant="contained"
              color="primary"
              disabled={confirmLoading}
              startIcon={
                confirmLoading ? <CircularProgress size={18} /> : undefined
              }
            >
              {language?.commons?.buttons?.yes || 'Evet'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={pointsModalOpen}
        onClose={() => setPointsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sorumlu Olduğu Birimler</DialogTitle>

        <DialogContent dividers>
          <div
            style={{
              maxHeight: 400,
              overflowY: 'auto',
            }}
          >
            {selectedPoints.map((point, index) => (
              <Typography key={index} sx={{ mb: 0.5 }}>
                {point.title}
              </Typography>
            ))}
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPointsModalOpen(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
